/**
 * vscroll.ts — the custom scrollbar (faint rail + dark thumb) used across the
 * configurator: the charm grid, the chains tab, the parking tray. Same look as the
 * reviews / saved-designs scrollbars. Appends a rail + thumb into a positioned
 * `container` and tracks the `scrollEl`'s scroll position. Auto-hides when it fits.
 */

const el = (tag: string, cls?: string) => { const e = document.createElement(tag); if (cls) e.className = cls; return e; };

/** Mount a scrollbar for `scrollEl` inside (relative) `container`. Returns an update fn. */
export function mountScrollbar(scrollEl: HTMLElement, container: HTMLElement, axis: 'y' | 'x' = 'y'): () => void {
  const v = axis === 'y';
  const rail = el('div', v
    ? 'absolute top-0 right-0 bottom-0 w-1 rounded-full bg-foreground/10 pointer-events-none opacity-0 transition-opacity duration-200'
    : 'absolute left-0 right-0 bottom-0 h-1 rounded-full bg-foreground/10 pointer-events-none opacity-0 transition-opacity duration-200');
  const thumb = el('div', v
    ? 'absolute right-0 w-1 rounded-full bg-foreground/55 pointer-events-none opacity-0 transition-opacity duration-200'
    : 'absolute bottom-0 h-1 rounded-full bg-foreground/55 pointer-events-none opacity-0 transition-opacity duration-200');
  container.append(rail, thumb);

  const update = (): void => {
    const max = v ? scrollEl.scrollHeight - scrollEl.clientHeight : scrollEl.scrollWidth - scrollEl.clientWidth;
    const show = max > 1;
    rail.style.opacity = show ? '1' : '0';
    thumb.style.opacity = show ? '1' : '0';
    if (!show) return;
    if (v) {
      const track = rail.clientHeight;
      const th = Math.max(24, (scrollEl.clientHeight / scrollEl.scrollHeight) * track);
      thumb.style.height = th + 'px';
      thumb.style.top = (scrollEl.scrollTop / max) * (track - th) + 'px';
    } else {
      const track = rail.clientWidth;
      const tw = Math.max(24, (scrollEl.clientWidth / scrollEl.scrollWidth) * track);
      thumb.style.width = tw + 'px';
      thumb.style.left = (scrollEl.scrollLeft / max) * (track - tw) + 'px';
    }
  };

  scrollEl.addEventListener('scroll', update);
  new ResizeObserver(update).observe(scrollEl);
  return update;
}
